import { money, type ISODateTime, type Money, type TaskId } from '../domain/branded.js';
import type { Baseline, BaselineTaskSnapshot } from '../domain/baseline.js';
import type { Diagnostic } from '../domain/errors.js';
import { EntityNotFoundError } from '../domain/errors.js';
import type {
  EacMethod,
  EarnedValueMetrics,
  EarnedValuePoint,
  EvmOptions,
  EvmResult,
  TaskEarnedValue,
  TcpiTarget,
} from '../domain/evm.js';
import type { Project } from '../domain/project.js';
import type { Task } from '../domain/task.js';
import { resolveCalendar, workingMinutesBetween, type ResolvedCalendar } from './calendar-engine.js';
import { parseDateTime } from './calendar-time.js';
import { computeCost } from './cost.js';

/**
 * SPI/CPI/TCPI 的唯一除法入口。
 *
 * 除了「分母為 0」之外，同時防守「運算元本身不是有限數字」與「相除結果不是
 * 有限數字」——三道防線都不通過就回傳 `null`。分母為 0 是預期會發生的正常情況
 * （專案剛啟動、AC 尚未發生……），但運算元非有限通常代表上游資料有缺陷（例如
 * 基準線快照缺欄位、外部匯入資料格式不對），這裡一律降級為 `null` 而不是讓
 * `NaN`/`Infinity` 流到回傳值裡去。
 */
function safeDivide(numerator: number, denominator: number): number | null {
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0) return null;
  const result = numerator / denominator;
  return Number.isFinite(result) ? result : null;
}

/** 把可能不是有限數字的原始值（例如外部資料缺欄位、格式錯誤）收斂成 Money，非有限一律歸零。 */
function finiteMoney(value: number): Money {
  return money(Number.isFinite(value) ? value : 0);
}

interface ResolvedStatusDate {
  readonly statusDate: ISODateTime;
  /** true 代表 `EvmOptions.statusDate` 與 `Project.statusDate` 都沒提供，退回了預設值。 */
  readonly usedFallback: boolean;
}

/**
 * 解析狀態日，**絕不拋錯**——這是刻意的一致性決定，不是疏漏。
 *
 * 這裡曾經在兩者皆缺時拋 `InvalidArgumentError`，理由是「core 不得自己做日期
 * 運算或用 `Date`」，而取得『現在』勢必要用 `Date`。但這個顧慮其實不成立：
 * `Project.finishDate` 是型別上必填、已經存在的欄位，用它當預設值完全不需要
 * 碰 `Date`——真正的問題從來不是「有沒有辦法給預設值」，而是「一開始選錯了
 * 預設值的來源」。
 *
 * 退回 `Project.finishDate` 的理由：EVM 沒有指定結算點時，「專案應該完工的
 * 那一天」是唯一不需要外部輸入、且對「這個專案目前的進度／預算表現如何」給
 * 出有意義答案的錨點——它讓 PV 意謂「若整個計畫如期執行，到完工為止應該花的
 * 預算」，等於整條基準線的 100%。這與本檔案其他所有路徑的一致承諾對齊：
 * 缺值不拋錯，退回一個有明確定義、可被 diagnostic 說明的預設。
 *
 * 呼叫端可透過回傳的 `usedFallback` 得知是否用了預設值；`computeEarnedValue()`
 * 會把它轉成 `EVM_NO_STATUS_DATE` diagnostic，讓 UI 能提示使用者「這份 EVM 是
 * 以 X 為結算點計算」。
 */
function resolveStatusDate(project: Project, options: EvmOptions): ResolvedStatusDate {
  const statusDate = options.statusDate ?? project.statusDate;
  if (statusDate !== null && statusDate !== undefined) {
    return { statusDate, usedFallback: false };
  }
  return { statusDate: project.finishDate, usedFallback: true };
}

/**
 * 依基準線區間與狀態日，用工作分鐘數比例計算 PV。
 *
 * 假設（契約已確認接受）：PV 一律採「時間比例攤提」，不依各 assignment 的
 * `accrueAt`。原因是 `BaselineAssignmentSnapshot` 沒有保存 accrueAt，基準線
 * 快照本身就不含這項資訊，因此 PV 無法、也不需要模擬 Start/Prorated/End 的
 * 差異——一律視為隨工作分鐘數（已扣除非工作時段的工期）線性展開。
 */
function proratedPv(
  calendar: ResolvedCalendar,
  bStart: ISODateTime,
  bFinish: ISODateTime,
  bCost: Money,
  statusDate: ISODateTime,
): Money {
  const cost = finiteMoney(bCost as number);
  if (bFinish <= statusDate) return cost;
  if (bStart >= statusDate) return money(0);
  const elapsed = Math.abs(workingMinutesBetween(calendar, bStart, statusDate) as number);
  const total = Math.abs(workingMinutesBetween(calendar, bStart, bFinish) as number);
  if (total === 0) return statusDate >= bStart ? cost : money(0);
  return finiteMoney((cost as number) * (elapsed / total));
}

// ---------------------------------------------------------------------------
// 基準線快照的統一、防禦性讀取
//
// `BaselineTaskSnapshot` 的欄位在型別上都是必填，但實際資料可能來自外部匯入
// （MSPDI）、手動建構、或未來版本的部分升級，型別檢查並不保證執行期真的有值。
// 這裡是**唯一**允許讀取快照數值欄位的地方：任何缺值／非有限數字／不是合法
// ISO 字串的欄位，一律在這裡就地收斂成明確預設值，絕不讓 `undefined`／`NaN`
// 流進下游的 PV/EV 運算。呼叫端只會拿到已經清洗過的 `SafeTaskSnapshot`。
// ---------------------------------------------------------------------------

interface SafeTaskSnapshot {
  readonly start: ISODateTime;
  readonly finish: ISODateTime;
  readonly cost: Money;
  /** 是否有欄位缺值／非有限而被收斂成預設值。 */
  readonly incomplete: boolean;
}

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/**
 * 驗證 ISO 日期時間字串是否合法——**格式驗證的唯一入口是 `calendar-time.ts` 的
 * `parseDateTime()`**，這裡不重寫任何格式規則，只是把它「拋錯代表不合法」的
 * 行為轉成布林值。這樣不合法日期的判定（分隔符、月/日/時/分/秒範圍、閏日等）
 * 永遠與日曆引擎本身的解析邏輯同步，不會出現兩套規則彼此漂移。
 *
 * 這點很關鍵：`workingMinutesBetween()` 等日曆函式對不合法輸入是**拋錯**的
 * （不會靜默正規化），所以任何畸形字串都不能被當成「非空字串就當作合法」而
 * 直接流進去——那會讓整個 EVM 計算被一個壞掉的基準線快照炸掉，破壞「絕不拋錯」
 * 的優雅降級承諾。
 */
const isValidIsoDateTime = (value: unknown): value is ISODateTime => {
  if (typeof value !== 'string' || value.length === 0) return false;
  try {
    parseDateTime(value as ISODateTime);
    return true;
  } catch {
    return false;
  }
};

/**
 * 結構性防護：`baseline.tasks` 本身可能不是陣列、或陣列中含 `null`/非物件項目
 * ——這種情況主要來自從 `.bproj` 檔案反序列化的資料（使用者手動編輯、磁碟
 * 損毀、舊版程式寫壞），不是 `captureBaseline()` 產生的資料（那個型別有保證）。
 *
 * 這裡只做**最低限度**的防護（不是陣列就當空陣列、篩掉 null/非物件項目），
 * 讓後面的 `.map()`/屬性存取不會直接炸掉。深度的欄位驗證屬於 `readTaskSnapshot()`
 * 已經在做的事；再更深的結構驗證（schema 驗證）是檔案載入層的責任，不屬於這裡。
 */
function sanitizeBaselineTasks(
  rawTasks: unknown,
  diagnostics: Diagnostic[],
): readonly BaselineTaskSnapshot[] {
  if (!Array.isArray(rawTasks)) {
    diagnostics.push({
      severity: 'Warning',
      code: 'EVM_BASELINE_SNAPSHOT_INCOMPLETE',
      message: 'Baseline.tasks is not an array (malformed baseline data); treated as empty.',
    });
    return [];
  }
  const clean: BaselineTaskSnapshot[] = [];
  let droppedCount = 0;
  for (const item of rawTasks) {
    if (item === null || typeof item !== 'object') {
      droppedCount += 1;
      continue;
    }
    clean.push(item as BaselineTaskSnapshot);
  }
  if (droppedCount > 0) {
    diagnostics.push({
      severity: 'Warning',
      code: 'EVM_BASELINE_SNAPSHOT_INCOMPLETE',
      message: `Baseline.tasks contained ${String(droppedCount)} null/non-object item(s) (malformed baseline data); they were skipped.`,
    });
  }
  return clean;
}

/**
 * 把（可能不完整的）`BaselineTaskSnapshot` 收斂成保證可安全參與運算的值。
 * `fallbackStart`/`fallbackFinish` 通常是對應 `Task` 目前的 start/finish——
 * 缺值或格式不合法時退回「視為與目前排程相同」比退回任意常數更合理，且能
 * 避免產生 `start > finish` 這種會讓 `proratedPv` 邏輯錯亂的組合。
 */
function readTaskSnapshot(
  snapshot: BaselineTaskSnapshot,
  fallbackStart: ISODateTime,
  fallbackFinish: ISODateTime,
): SafeTaskSnapshot {
  const validStart = isValidIsoDateTime(snapshot.start);
  const validFinish = isValidIsoDateTime(snapshot.finish);
  const validCost = isFiniteNumber(snapshot.cost);
  return {
    start: validStart ? snapshot.start : fallbackStart,
    finish: validFinish ? snapshot.finish : fallbackFinish,
    cost: money(validCost ? (snapshot.cost as number) : 0),
    incomplete: !validStart || !validFinish || !validCost,
  };
}

function eacFor(method: EacMethod, bac: Money, ev: Money, ac: Money, cpi: number | null, spi: number | null): Money {
  const bacN = bac as number;
  const evN = ev as number;
  const acN = ac as number;
  const additive = acN + (bacN - evN);

  if (method === 'Additive') return money(additive);
  if (method === 'CpiBased') return money(cpi === null || cpi === 0 ? additive : bacN / cpi);
  // CpiSpiBased
  if (cpi === null || spi === null || cpi * spi === 0) return money(additive);
  return money(acN + (bacN - evN) / (cpi * spi));
}

function buildMetrics(
  pvIn: Money,
  evIn: Money,
  acIn: Money,
  bacIn: Money,
  eacMethod: EacMethod,
  tcpiTarget: TcpiTarget,
): EarnedValueMetrics {
  // 防禦性收斂：無論呼叫端傳入的數值是否已經過清洗，這裡一律再收斂一次，
  // 確保本函式的輸出不可能因為輸入帶著 NaN/Infinity 而外洩。
  const pv = finiteMoney(pvIn as number);
  const ev = finiteMoney(evIn as number);
  const ac = finiteMoney(acIn as number);
  const bac = finiteMoney(bacIn as number);

  const spi = safeDivide(ev as number, pv as number);
  const cpi = safeDivide(ev as number, ac as number);
  const eac = finiteMoney(eacFor(eacMethod, bac, ev, ac, cpi, spi) as number);
  const vac = finiteMoney((bac as number) - (eac as number));
  const etc = finiteMoney((eac as number) - (ac as number));
  const tcpiDenominator = tcpiTarget === 'BAC' ? (bac as number) - (ac as number) : (eac as number) - (ac as number);
  const tcpi = safeDivide((bac as number) - (ev as number), tcpiDenominator);

  return {
    pv,
    ev,
    ac,
    sv: finiteMoney((ev as number) - (pv as number)),
    cv: finiteMoney((ev as number) - (ac as number)),
    spi,
    cpi,
    bac,
    eac,
    etc,
    vac,
    tcpi,
  };
}

interface LeafEvm {
  readonly taskId: TaskId;
  readonly pv: Money;
  readonly ev: Money;
  readonly ac: Money;
  readonly bac: Money;
}

/**
 * 指定的基準線編號不存在（例如剛被 `clearBaseline()` 清除）時的優雅降級結果：
 * 所有 PV/EV/AC/BAC 皆為 0，SPI/CPI/TCPI 依 0/0 規則回傳 `null`（不拋錯、不產生
 * NaN/Infinity），並附上 `EVM_NO_BASELINE` diagnostic。
 *
 * 這個 diagnostic 的存在很關鍵：光看 `pv=0/ev=0/ac=0` 無法區分「這個專案真的
 * 沒有成本」跟「還沒設定基準線，所以算不出來」——兩者對使用者的正確反應完全
 * 不同（前者不用做事，後者要去按「設定基準」）。UI 必須用這個 diagnostic 而非
 * 猜測數值背後的原因。
 */
function degradedResult(
  project: Project,
  statusDate: ISODateTime,
  options: EvmOptions,
  diagnostics: Diagnostic[],
): EvmResult {
  const zero = buildMetrics(money(0), money(0), money(0), money(0), options.eacMethod, options.tcpiTarget);
  diagnostics.push({
    severity: 'Warning',
    code: 'EVM_NO_BASELINE',
    message:
      `Baseline ${String(options.baselineNumber)} is not set for this project. ` +
      'Earned value metrics cannot be calculated until a baseline is saved; ' +
      'all figures below are reported as 0, not an actual zero cost.',
  });
  return {
    statusDate,
    baselineNumber: options.baselineNumber,
    project: zero,
    byTask: project.tasks.map((task) => ({ taskId: task.id, ...zero })),
    curve: [],
    diagnostics,
  };
}

/** 計算實獲值分析結果。 */
export function computeEarnedValue(project: Project, options: EvmOptions): EvmResult {
  const { statusDate, usedFallback: noStatusDate } = resolveStatusDate(project, options);
  const diagnostics: Diagnostic[] = [];
  if (noStatusDate) {
    diagnostics.push({
      severity: 'Warning',
      code: 'EVM_NO_STATUS_DATE',
      message:
        'Neither EvmOptions.statusDate nor Project.statusDate was provided; ' +
        `defaulted to the project finish date (${statusDate as string}) as the cutoff point. ` +
        'PV/EV/AC below reflect the plan as if evaluated at project completion, not "today".',
    });
  }

  const baseline = project.baselines.find((b) => b.number === options.baselineNumber);
  if (baseline === undefined) {
    return degradedResult(project, statusDate, options, diagnostics);
  }

  const calendarsById = new Map(project.calendars.map((c) => [c.id as string, c]));
  const resolvedCalendarCache = new Map<string, ResolvedCalendar>();
  const resolveCal = (calendarId: string): ResolvedCalendar => {
    const cached = resolvedCalendarCache.get(calendarId);
    if (cached !== undefined) return cached;
    const cal = calendarsById.get(calendarId);
    if (cal === undefined) throw new EntityNotFoundError('CALENDAR_NOT_FOUND', calendarId);
    const resolved = resolveCalendar(cal, calendarsById);
    resolvedCalendarCache.set(calendarId, resolved);
    return resolved;
  };

  // 結構性防護放在最前面：baseline.tasks 若本身結構就是壞的（非陣列／含
  // null 項目——通常來自 .bproj 反序列化的損壞資料），先在這裡收斂成乾淨的
  // 陣列，後面所有讀取（包含 buildCurve）都只看這份清洗過的資料，不再碰
  // 原始的 `baseline.tasks`。
  const safeBaselineTasks = sanitizeBaselineTasks(baseline.tasks, diagnostics);
  const sanitizedBaseline: Baseline = { ...baseline, tasks: safeBaselineTasks };

  const baselineTaskById = new Map(safeBaselineTasks.map((snap) => [snap.taskId as string, snap]));

  // AC 一律透過成本引擎計算，確保與 computeCost() 的 accrueAt 語意一致。
  const costResult = computeCost({ project, options: { statusDate, baselineNumber: options.baselineNumber } });
  const acByTaskId = new Map(costResult.byTask.map((tc) => [tc.taskId as string, tc.actualCost as number]));

  const leaves: LeafEvm[] = [];

  for (const task of project.tasks) {
    if (task.isSummary) continue;
    const rawAc = acByTaskId.get(task.id as string);
    const ac = finiteMoney(rawAc ?? 0);

    const snapshot = baselineTaskById.get(task.id as string);
    if (snapshot === undefined) {
      // 基準線建立後才新增的任務：沒有預算，PV/EV/BAC 皆為 0，但仍計入 AC。
      leaves.push({ taskId: task.id, pv: money(0), ev: money(0), ac, bac: money(0) });
      continue;
    }

    // 統一、防禦性的快照讀取：缺值／非有限一律收斂成明確預設，絕不讓
    // `undefined`／`NaN` 流進 proratedPv() 或下面的乘法。
    const safe = readTaskSnapshot(snapshot, task.start, task.finish);
    if (safe.incomplete) {
      diagnostics.push({
        severity: 'Warning',
        code: 'EVM_BASELINE_SNAPSHOT_INCOMPLETE',
        message:
          `Baseline snapshot for task ${task.id as string} is missing or has non-finite start/finish/cost; ` +
          'missing fields were defaulted (dates fall back to the task\'s current schedule, cost falls back to 0). ' +
          'PV/EV for this task may be understated.',
        taskId: task.id,
      });
    }

    const calendarId = (task.calendarId ?? project.defaultCalendarId) as string;
    const calendar = resolveCal(calendarId);
    const pv = proratedPv(calendar, safe.start, safe.finish, safe.cost, statusDate);
    const percentComplete = isFiniteNumber(task.percentComplete) ? (task.percentComplete as number) : 0;
    const ev = finiteMoney((safe.cost as number) * (percentComplete / 100));
    leaves.push({ taskId: task.id, pv, ev, ac, bac: safe.cost });
  }

  const leafById = new Map(leaves.map((l) => [l.taskId as string, l]));

  // 摘要任務：遞迴彙總子孫葉節點。
  const childrenByParent = new Map<string, Task[]>();
  for (const task of project.tasks) {
    if (task.parentId === null) continue;
    const key = task.parentId as string;
    const list = childrenByParent.get(key) ?? [];
    list.push(task);
    childrenByParent.set(key, list);
  }

  function rollup(taskId: string): { pv: number; ev: number; ac: number; bac: number } {
    const leaf = leafById.get(taskId);
    if (leaf !== undefined) {
      return { pv: leaf.pv as number, ev: leaf.ev as number, ac: leaf.ac as number, bac: leaf.bac as number };
    }
    const children = childrenByParent.get(taskId) ?? [];
    let pv = 0;
    let ev = 0;
    let ac = 0;
    let bac = 0;
    for (const child of children) {
      const r = rollup(child.id as string);
      pv += r.pv;
      ev += r.ev;
      ac += r.ac;
      bac += r.bac;
    }
    return { pv, ev, ac, bac };
  }

  const byTask: TaskEarnedValue[] = project.tasks.map((task) => {
    const r = rollup(task.id as string);
    return {
      taskId: task.id,
      ...buildMetrics(money(r.pv), money(r.ev), money(r.ac), money(r.bac), options.eacMethod, options.tcpiTarget),
    };
  });

  const projectTotals = leaves.reduce(
    (acc, l) => ({
      pv: acc.pv + (l.pv as number),
      ev: acc.ev + (l.ev as number),
      ac: acc.ac + (l.ac as number),
      bac: acc.bac + (l.bac as number),
    }),
    { pv: 0, ev: 0, ac: 0, bac: 0 },
  );

  const projectMetrics = buildMetrics(
    money(projectTotals.pv),
    money(projectTotals.ev),
    money(projectTotals.ac),
    money(projectTotals.bac),
    options.eacMethod,
    options.tcpiTarget,
  );

  if (projectMetrics.spi === null) {
    diagnostics.push({ severity: 'Info', code: 'EVM_ZERO_PV', message: 'PV is 0; SPI is undefined (reported as null).' });
  }
  if (projectMetrics.cpi === null) {
    diagnostics.push({ severity: 'Info', code: 'EVM_ZERO_AC', message: 'AC is 0; CPI is undefined (reported as null).' });
  }
  if (projectMetrics.tcpi === null) {
    diagnostics.push({
      severity: 'Info',
      code: 'EVM_ZERO_TCPI_DENOMINATOR',
      message: 'TCPI denominator is 0; TCPI is undefined (reported as null).',
    });
  }

  const curve = buildCurve(leaves, sanitizedBaseline, statusDate, resolveCal, project);

  return {
    statusDate,
    baselineNumber: options.baselineNumber,
    project: projectMetrics,
    byTask,
    curve,
    diagnostics,
  };
}

/**
 * 產生 S 曲線資料點：以基準線任務的 start/finish 邊界與 statusDate 為取樣點（去重排序）。
 * PV 沿用任務層級同一套「時間比例攤提」假設；EV/AC 在 statusDate 之前尚未發生（0），
 * statusDate（含）之後採用狀態日當下的最終值（沒有逐日的歷史實際資料可供回溯）。
 */
function buildCurve(
  leaves: readonly LeafEvm[],
  baseline: Baseline,
  statusDate: ISODateTime,
  resolveCal: (calendarId: string) => ResolvedCalendar,
  project: Project,
): EarnedValuePoint[] {
  const taskById = new Map(project.tasks.map((t) => [t.id as string, t]));
  const snapshotByTaskId = new Map(baseline.tasks.map((s) => [s.taskId as string, s]));

  const dateSet = new Set<string>();
  for (const snap of baseline.tasks) {
    const task = taskById.get(snap.taskId as string);
    const fallbackStart = task?.start ?? statusDate;
    const fallbackFinish = task?.finish ?? statusDate;
    const safe = readTaskSnapshot(snap, fallbackStart, fallbackFinish);
    dateSet.add(safe.start as string);
    dateSet.add(safe.finish as string);
  }
  dateSet.add(statusDate as string);
  const dates = [...dateSet].sort() as ISODateTime[];

  return dates.map((date) => {
    let pv = 0;
    let ev = 0;
    let ac = 0;
    for (const leaf of leaves) {
      const snap = snapshotByTaskId.get(leaf.taskId as string);
      if (snap !== undefined) {
        const task = taskById.get(leaf.taskId as string);
        const fallbackStart = task?.start ?? statusDate;
        const fallbackFinish = task?.finish ?? statusDate;
        const safe = readTaskSnapshot(snap, fallbackStart, fallbackFinish);
        const calendarId = (task?.calendarId ?? project.defaultCalendarId) as string;
        pv += proratedPv(resolveCal(calendarId), safe.start, safe.finish, safe.cost, date) as number;
      }
      if (date >= statusDate) {
        ev += leaf.ev as number;
        ac += leaf.ac as number;
      }
    }
    return { date, pv: finiteMoney(pv), ev: finiteMoney(ev), ac: finiteMoney(ac) };
  });
}
